import { useQuery } from '@tanstack/react-query';
import { getClinicSite } from './clinicSite';
import { useDtpAuthStore } from '../store/useDtpAuthStore';

/**
 * Ava in the patient portal (backend routes/dtp/ava.ts). Plain fetch rather
 * than dtpApi's axios client because replies stream as Server-Sent Events.
 */

export type PatientAvaDestination = 'support_chat' | 'care_team' | 'orders' | 'refills' | 'treatments' | 'results' | 'billing' | 'account' | 'membership' | 'shop';

export type PatientAvaEvent =
  | { type: 'conversation'; conversationId: string }
  | { type: 'text'; text: string }
  | { type: 'tool'; name: string }
  | { type: 'next_step'; destination: PatientAvaDestination; label: string }
  | { type: 'done'; conversationId: string; stopReason: string | null }
  | { type: 'error'; message: string };

const base = () => (getClinicSite() ? '/api/dtp' : `${import.meta.env.VITE_API_URL || '/api'}/dtp`);
function headers(json = false): Record<string, string> {
  const token = localStorage.getItem('dtp-token');
  return { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...(json ? { 'Content-Type': 'application/json' } : {}) };
}
async function get<T>(path: string): Promise<T> {
  const res = await fetch(`${base()}${path}`, { headers: headers() });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || !json.success) throw new Error(json.error || `Request failed (${res.status})`);
  return json.data as T;
}

export const patientAva = {
  status: () => get<{ available: boolean; starters?: string[] }>('/ava/status'),

  /** Send a message; the reply arrives as a stream of events. */
  async chat(body: { conversationId?: string; message: string }, onEvent: (event: PatientAvaEvent) => void, signal?: AbortSignal): Promise<void> {
    const res = await fetch(`${base()}/ava/chat`, { method: 'POST', headers: headers(true), body: JSON.stringify(body), signal });
    if (!res.ok || !res.body) {
      const json = await res.json().catch(() => ({}));
      throw new Error(json.error || `Request failed (${res.status})`);
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let boundary: number;
      while ((boundary = buffer.indexOf('\n\n')) !== -1) {
        const chunk = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);
        const data = chunk.split('\n').filter(l => l.startsWith('data: ')).map(l => l.slice(6)).join('\n');
        if (data) onEvent(JSON.parse(data));
      }
    }
  },
};

/** Whether Ava is on for this patient. Never for a clinic viewing the portal. */
export function usePatientAvaStatus() {
  const isAuthenticated = useDtpAuthStore(s => s.isAuthenticated);
  const impersonated = useDtpAuthStore(s => s.impersonated);
  const patientId = useDtpAuthStore(s => s.patient?._id);
  const query = useQuery({
    queryKey: ['patient-ava-status', patientId],
    enabled: Boolean(isAuthenticated && !impersonated && patientId),
    staleTime: 10 * 60_000,
    retry: false,
    queryFn: () => patientAva.status(),
  });
  return { available: Boolean(query.data?.available) && !impersonated, starters: query.data?.starters ?? [] };
}
